import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Leaf, Flame } from "lucide-react";
import dayjs from "dayjs";
import { ContainerCard } from "../../components/ui/cards";
import useApiMethods from "../../lib/useApiMethods";
import { NURTURE_REPORT_URL } from "../../constants/api";
import { cn } from "../../utils/tailwindUtils";
import { Stat, inputCls } from "../../components/assignment/shared";

const pctText = (v) => (v === null || v === undefined ? "—" : `${v}%`);
const date = (d) => (d ? dayjs(d).format("D MMM YYYY") : "—");

/**
 * Reports -> Nurture. Who is parked in the Nurture stage now (revisit due and
 * warming up first), and what the period's nurture came to: stays started,
 * revived, won, messages sent, opened, clicked, unsubscribed.
 * email/nurture/report/ (platform_core/email_integration/nurture_views.py).
 */
export default function NurtureReport() {
    const { fetch } = useApiMethods();
    const navigate = useNavigate();
    const [range, setRange] = useState({
        from: dayjs().subtract(89, "day").format("YYYY-MM-DD"),
        to: dayjs().format("YYYY-MM-DD"),
    });
    const [data, setData] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        setData(null);
        fetch(NURTURE_REPORT_URL, range).then(res => {
            if (res?.status === "success") { setData(res.data); setError(""); }
            else setError(res?.message || "Could not load the report");
        });
    }, [range]); // eslint-disable-line react-hooks/exhaustive-deps

    const openLead = (id) => navigate(`/customers/customerdetails?id=${id}`, { state: { formEdit: true, row: { id } } });
    const s = data?.summary;
    // Revisit due and warm leads first: they are the ones to call.
    const rows = data ? [...data.rows].sort((a, b) =>
        (Number(!!b.revisit_due || !!b.warm_at) - Number(!!a.revisit_due || !!a.warm_at))
        || String(a.revisit_on || "9999").localeCompare(String(b.revisit_on || "9999"))) : [];

    return (
        <ContainerCard>
            <div className="p-4 space-y-5 overflow-auto">
                <div className="flex flex-wrap items-end justify-between gap-3">
                    <div className="flex items-center gap-2">
                        <Leaf className="h-5 w-5 text-primary" />
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Nurture</h2>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                Real leads parked for later — who to call back now, and what the nurture messages achieved.
                            </p>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <input type="date" value={range.from} aria-label="From"
                            onChange={e => setRange(r => ({ ...r, from: e.target.value }))} className={cn(inputCls, "w-36")} />
                        <input type="date" value={range.to} aria-label="To"
                            onChange={e => setRange(r => ({ ...r, to: e.target.value }))} className={cn(inputCls, "w-36")} />
                    </div>
                </div>

                {error && <p className="text-xs text-gray-500">{error}</p>}
                {!data && !error && <p className="text-xs text-gray-400">Loading…</p>}

                {data && (
                    <>
                        <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
                            <Stat label="In Nurture now" value={s.in_nurture} />
                            <Stat label="Revisit due" value={s.revisit_due} tone={s.revisit_due ? "warn" : undefined}
                                hint="Revisit date today or passed" />
                            <Stat label="Warming up" value={s.warm} hint="Clicked, replied or enquired again" />
                            <Stat label="Parked in period" value={s.started} />
                            <Stat label="Revived" value={s.revived} hint={`${s.revived_then_won} since won`} />
                            <Stat label="Won from Nurture Lead" value={s.won} hint={`${s.closed} closed instead`} />
                        </div>
                        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                            <Stat label="Emails sent" value={s.emails_sent} />
                            <Stat label="Opened" value={pctText(s.open_rate)} hint={`${s.emails_opened} emails`} />
                            <Stat label="Clicked" value={pctText(s.click_rate)} hint={`${s.emails_clicked} emails`} />
                            <Stat label="WhatsApp sent" value={s.whatsapp_sent} />
                            <Stat label="Unsubscribed" value={s.unsubscribed}
                                hint={`${s.skipped} not sent · ${s.failed} failed`} tone={s.failed ? "warn" : undefined} />
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                            {[["Why they are parked", data.by_reason], ["By owner", data.by_owner]].map(([title, list]) => (
                                <div key={title} className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
                                    <h3 className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-2">{title}</h3>
                                    {list.length === 0 ? <p className="text-xs text-gray-400">Nobody in Nurture Lead.</p> : (
                                        <ul className="space-y-1">
                                            {list.map(r => (
                                                <li key={r.label} className="flex justify-between text-xs text-gray-700 dark:text-gray-200">
                                                    <span>{r.label}</span><span className="font-semibold">{r.count}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            ))}
                        </div>

                        {rows.length === 0 ? (
                            <p className="text-xs text-gray-400">No leads in Nurture Lead. Move a real lead who doesn't want it now to the Nurture Lead stage.</p>
                        ) : (
                            <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                                <table className="min-w-full text-xs">
                                    <thead className="bg-gray-50 dark:bg-gray-700/40 text-gray-500">
                                        <tr>{["Lead", "Owner", "Reason", "Since", "Revisit", "Signal", "Last message"].map(c =>
                                            <th key={c} className="px-3 py-2 text-left font-medium">{c}</th>)}</tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                        {rows.map(r => (
                                            <tr key={r.customer_id}>
                                                <td className="px-3 py-2">
                                                    <button type="button" onClick={() => openLead(r.customer_id)}
                                                        className="text-brand-600 dark:text-brand-300 hover:underline text-left">
                                                        {r.customer_name || `Lead #${r.customer_id}`}
                                                    </button>
                                                </td>
                                                <td className="px-3 py-2 text-gray-700 dark:text-gray-200">{r.owner}</td>
                                                <td className="px-3 py-2 text-gray-700 dark:text-gray-200">{r.reason || "—"}</td>
                                                <td className="px-3 py-2 whitespace-nowrap text-gray-700 dark:text-gray-200">{date(r.since)}</td>
                                                <td className={cn("px-3 py-2 whitespace-nowrap", r.revisit_due
                                                    ? "font-semibold text-amber-700 dark:text-amber-300" : "text-gray-700 dark:text-gray-200")}>
                                                    {date(r.revisit_on)}{r.revisit_due && " · due"}
                                                </td>
                                                <td className="px-3 py-2 text-gray-700 dark:text-gray-200">
                                                    {r.warm_at ? (
                                                        <span className="inline-flex items-start gap-1 text-orange-700 dark:text-orange-300" title={r.warm_reason}>
                                                            <Flame className="h-3.5 w-3.5 shrink-0" aria-hidden="true" /> {date(r.warm_at)}
                                                        </span>
                                                    ) : "—"}
                                                </td>
                                                <td className="px-3 py-2 whitespace-nowrap text-gray-700 dark:text-gray-200">{date(r.last_sent_at)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </>
                )}
            </div>
        </ContainerCard>
    );
}
